"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { JSX, useCallback, useRef, useState } from "react";
import useIsMobile from "@@/hooks/useIsMobile";
import styles from "@@/styles/components/Footer.module.scss";
import { exo2Medium, exo2SemiBold } from "@@/utils/fonts";
import MediawindLogo from "@@/components/ui/MediawindLogo";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import Button from "@@/components/ui/Button";
import imgTeam4k from "@@/public/img/about/why/team-4k.webp";
import imgTeam from "@@/public/img/about/why/team-lowres.webp";
import fbLogo from "@@/public/img/facebook.png";
import linkedinLogo from "@@/public/img/linkedin.png";

export default function Footer(): JSX.Element {
	const { t } = useTranslation("footer");
	const [isMobile] = useIsMobile();

	// ---- Obfuscation email/phone ----
	// on stocke les parties séparées pour éviter une chaîne directe dans le HTML
	const emailUser = "info";
	const emailDomain = "mediawind";
	const emailTld = "be";
	// tel: on sépare en morceaux ; ce que l’on affiche peut rester formaté humainement
	const telParts = ["+32", "67", "49", "35", "00"]; // affichage
	const telHrefDigits = "+3267493500";              // pour href tel:

	const [emailHref, setEmailHref] = useState<string | null>(null);
	const [emailText, setEmailText] = useState<string>("info@mediawind.be");

	const [telHref, setTelHref] = useState<string | null>(null);
	const [telText, setTelText] = useState<string>("+32 (0)67/49.35.00");

	const emailLinkRef = useRef<HTMLAnchorElement | null>(null);
	const telLinkRef = useRef<HTMLAnchorElement | null>(null);

	const revealEmail = useCallback(() => {
		if (emailHref) return; // déjà fait
		const real = `${emailUser}@${emailDomain}.${emailTld}`;
		setEmailHref(`mailto:${real}`);
		setEmailText(real);
	}, [emailHref]);

	const revealTel = useCallback(() => {
		if (telHref) return; // déjà fait
		setTelHref(`tel:${telHrefDigits}`);
		// tu peux garder le format humain pour le texte
		setTelText(`${telParts[0]} (0)${telParts[1]}/${telParts[2]}.${telParts[3]}.${telParts[4]}`);
	}, [telHref]);

	// accessibilité : si l’utilisateur “active” avec Enter/Espace alors on “révèle” et on déclenche la navigation
	const onEmailKey = useCallback(
		(e: React.KeyboardEvent<HTMLAnchorElement>) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				revealEmail();
				// après révélation, on lance la navigation
				const target = emailLinkRef.current;
				if (target) {
					// petite micro-tâche pour laisser React appliquer le setState
					setTimeout(() => target.click(), 0);
				}
			}
		},
		[revealEmail]
	);

	const onTelKey = useCallback(
		(e: React.KeyboardEvent<HTMLAnchorElement>) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				revealTel();
				const target = telLinkRef.current;
				if (target) {
					setTimeout(() => target.click(), 0);
				}
			}
		},
		[revealTel]
	);

	const addressContent = (
		<address className={styles.address}>
			<p>Rue de l&apos;Industrie, 30A</p>
			<p>1400 {t("nivelles")}</p>
			<p>{t("belgium")}</p>
		</address>
	);

	return (
		<footer className={styles.main}>
			<div className={`${styles.row} mb-5 gap-5 w-100`}>
				<div className={`d-flex flex-column gap-2 flex-1 mx-w-100-mobile min-w-100-mobile justify-center-mobile`}>
					<h2
						className={`${exo2SemiBold.className} ${styles.titleHero} w-fit`}
						dangerouslySetInnerHTML={{ __html: t("hero_title") }}
					/>
					<Button href={"/contact"} bgColor={`var(--grey-000)`} className={``} textColor={`var(--blue-900)`} title={t("btn_hero_text")} />
				</div>
				<ProgressiveImage
					lowSrc={imgTeam}
					highSrc={imgTeam4k}
					alt={t("image_hero_alt")}
					classLowRes={`lowRes`}
					width={(imgTeam4k.width ?? 1920)}
					height={(imgTeam4k.height ?? 1080)}
					quality={100}
					className={`${styles.imgHero} flex-03 mx-width-40 min-h-20 mx-w-100-mobile min-w-100-mobile`}
				/>
			</div>

			<div className={styles.row}>
				<div className={`${styles.logo} justify-center-mobile min-w-100-mobile`}>
					<MediawindLogo scrollTop={true} backgroundIsWhite={false} isMobile={false} isFooter={true} />
					<p className={`mx-width-30 justify-center-mobile`}>{t("footer_slogan")}</p>
				</div>

				<div className={styles.footerContent}>
					<div className={styles.contact}>
						<h2 className={`${exo2Medium.className} ${styles.navBlockTitle}`}>{t("our_contact_informations")}</h2>

						<div className={styles.navListItem}>
							<p>
								<a
									ref={telLinkRef}
									href={telHref || "#"}
									className={styles.link}
									onMouseEnter={revealTel}
									onFocus={revealTel}
									onClick={(e) => {
										if (!telHref) {
											e.preventDefault();
											revealTel();
										}
									}}
									onKeyDown={onTelKey}
									rel="nofollow"
								>
									{telText}
								</a>
							</p>

							<p id={styles.email_address}>
								<a
									ref={emailLinkRef}
									href={emailHref || "#"}
									className={styles.link}
									onMouseEnter={revealEmail}
									onFocus={revealEmail}
									onClick={(e) => {
										if (!emailHref) {
											e.preventDefault();
											revealEmail();
										}
									}}
									onKeyDown={onEmailKey}
									rel="nofollow"
								>
									{emailText}
								</a>
								<noscript>
									<span>info [at] mediawind [dot] be</span>
								</noscript>
							</p>
						</div>

						<div className={styles.socials_links}>
							<Link href={"https://www.facebook.com/Mediawind.be/"} target="_blank" rel="noopener noreferrer nofollow">
								<Image src={fbLogo} alt="Facebook logo" height={20} />
							</Link>
							<Link href={"https://be.linkedin.com/company/mediawind"} target="_blank" rel="noopener noreferrer nofollow">
								<Image src={linkedinLogo} alt="LinkedIn logo" height={20} />
							</Link>
						</div>
					</div>

					{/* -------- Navigation -------- */}
					<div className={styles.solutions}>
						<h2 className={`${exo2Medium.className} ${styles.navBlockTitle}`}>{t("quick_nav")}</h2>
						<ul>
							<li><Link href={"/"} className={styles.link}>{t("home")}</Link></li>
							<li><Link href={"/about"} className={styles.link}>{t("about_us")}</Link></li>
							<li><Link href={"/developpement_&_consultance"} className={styles.link}>{t("dev&cons")}</Link></li>
							<li><Link href={"/contact"} className={styles.link}>{t("contact_us")}</Link></li>
						</ul>
					</div>

					<div className={styles.services}>
						<h2 className={`${exo2Medium.className} ${styles.navBlockTitle}`}>{t("our_sectors")}</h2>
						<ul>
							<li><Link href={"/sectors/hospital_medical"} className={styles.link}>{t("medical")}</Link></li>
							<li><Link href={"/sectors/offices_companies"} className={styles.link}>{t("company")}</Link></li>
							<li><Link href={"/sectors/airport"} className={styles.link}>{t("airport")}</Link></li>
							<li><Link href={"/sectors/airport"} className={styles.link}>{t("transport")}</Link></li>
							<li><Link href={"/sectors/airport"} className={styles.link}>{t("cities")}</Link></li>
							<li><Link href={"/sectors/airport"} className={styles.link}>{t("eductation")}</Link></li>
						</ul>
					</div>

					<div className={styles.solutions}>
						<h2 className={`${exo2Medium.className} ${styles.navBlockTitle}`}>{t("adress")}</h2>
						{isMobile ? (
							<a href="geo:50.587383,4.355017" className={styles.link}>{addressContent}</a>
						) : (
							<Link
								href="https://maps.app.goo.gl/xkHgYZKEo3pqV3eE7"
								className={styles.link}
								target="_blank"
								rel="noopener noreferrer"
							>
								{addressContent}
							</Link>
						)}
					</div>

					<div className={styles.navBlock}>
						<h2 className={`${exo2Medium.className} ${styles.navBlockTitle}`}>{t("our_solutions")}</h2>
						<ul>
							<li><Link href={"/solutions/greenplayer"} className={styles.link}>{t("greenplayer")}</Link></li>
							<li><Link href={"/solutions/kiosks"} className={styles.link}>{t("bornes")}</Link></li>
							<li><Link href={"/solutions/screens"} className={styles.link}>{t("screens")}</Link></li>
							<li><Link href={"/solutions/totems"} className={styles.link}>{t("totems")}</Link></li>
						</ul>
					</div>
				</div>
			</div>

			<div className={styles.row}>
				<p className={styles.copyright}>
					Copyright &copy; {new Date().getFullYear()} Mediawind - {t("all_rights_reserved")}
				</p>
			</div>
		</footer>
	);
}
